import React from 'react';
import { Video, Shield, Clock, Globe, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import ServicePageLayout from './ServicePageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export default function VideoConsultations() {
  const { t } = useTranslation();
  
  return (
    <ServicePageLayout
      title={t('services.videoConsultations.title')}
      subtitle={t('services.videoConsultations.subtitle')}
      icon={Video}
      heroColor="from-indigo-500 to-blue-600"
      enquiryTitle={t('services.videoConsultations.title')}
    >
      {/* Main Description */}
      <Card className="mb-8 shadow-lg" data-testid="service-main-card">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('services.videoConsultations.mainTitle')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            {t('services.videoConsultations.mainDesc1')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('services.videoConsultations.mainDesc2')}
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-secure">
          <CardContent className="p-6">
            <Shield className="w-12 h-12 text-indigo-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.videoConsultations.features.secure.title')}</h3>
            <p className="text-gray-700">
              {t('services.videoConsultations.features.secure.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-convenient">
          <CardContent className="p-6">
            <Clock className="w-12 h-12 text-indigo-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.videoConsultations.features.convenient.title')}</h3>
            <p className="text-gray-700">
              {t('services.videoConsultations.features.convenient.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-global">
          <CardContent className="p-6">
            <Globe className="w-12 h-12 text-indigo-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.videoConsultations.features.global.title')}</h3>
            <p className="text-gray-700">
              {t('services.videoConsultations.features.global.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-easy">
          <CardContent className="p-6">
            <Video className="w-12 h-12 text-indigo-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.videoConsultations.features.easy.title')}</h3>
            <p className="text-gray-700">
              {t('services.videoConsultations.features.easy.desc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* What's Included */}
      <Card className="mb-8 shadow-lg" data-testid="included-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.whatsIncluded')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t('services.videoConsultations.included').map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Consultation Types */}
      <Card className="mb-8 shadow-lg" data-testid="types-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.videoConsultations.typesTitle')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.videoConsultations.types.initial.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.videoConsultations.types.initial.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.videoConsultations.types.initial.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.videoConsultations.types.followUp.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.videoConsultations.types.followUp.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.videoConsultations.types.followUp.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.videoConsultations.types.secondOpinion.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.videoConsultations.types.secondOpinion.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.videoConsultations.types.secondOpinion.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Specialties */}
      <Card className="mb-8 shadow-lg" data-testid="specialties-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.videoConsultations.specialtiesTitle')}</h2>
          <div className="flex flex-wrap gap-3">
            {t('services.videoConsultations.specialtiesList').map((specialty) => (
              <span 
                key={specialty} 
                className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium border border-indigo-200"
              >
                {specialty}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </ServicePageLayout>
  );
}
